import { z } from 'zod';
import type { ShapeManifest } from '../../../../lib/diagram/shape-manifest.types';
import {
  escapeSvgAttr,
  formatSvgNumber,
  renderShapeLabel,
  resolveShapePresentation,
} from '../svgRenderer.utils';

type BasicVariant =
  | 'rectangle'
  | 'circle'
  | 'triangle'
  | 'diamond'
  | 'hexagon'
  | 'pentagon'
  | 'star';

const BASIC_DEFAULTS = {
  fill: '#FFFFFF',
  stroke: '#1E293B',
  strokeWidth: 2,
  textColor: '#0F172A',
  fontSize: 14,
};

const TEXT_PADDING = 12;

const clampDimension = (value: number | undefined, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  return fallback;
};

const buildPolygonPoints = (sides: number, width: number, height: number): string => {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(1, Math.min(width, height) / 2);
  const points: Array<string> = [];
  for (let i = 0; i < sides; i += 1) {
    const angle = (-Math.PI / 2) + (i * (2 * Math.PI)) / sides;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    points.push(`${formatSvgNumber(x)},${formatSvgNumber(y)}`);
  }
  return points.join(' ');
};

const buildStarPoints = (numPoints: number, width: number, height: number): string => {
  const cx = width / 2;
  const cy = height / 2;
  const outerRadius = Math.max(1, Math.min(width, height) / 2);
  const innerRadius = outerRadius * 0.5;
  const points: Array<string> = [];
  const total = numPoints * 2;
  for (let i = 0; i < total; i += 1) {
    const angle = (-Math.PI / 2) + (i * Math.PI) / numPoints;
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    points.push(`${formatSvgNumber(x)},${formatSvgNumber(y)}`);
  }
  return points.join(' ');
};

const renderBasicShapeSVG = (
  variant: BasicVariant,
  dimensions: { width: number; height: number },
  args: {
    viewModel: { width?: number; height?: number; style?: Record<string, unknown> };
    props: z.infer<typeof basicShapeProps> & { attrs?: Record<string, unknown> };
  },
): string => {
  const width = clampDimension(args.viewModel?.width, dimensions.width);
  const height = clampDimension(args.viewModel?.height, dimensions.height);
  const presentation = resolveShapePresentation(args.props, args.viewModel, BASIC_DEFAULTS);

  const dashAttr = presentation.dashEnabled && presentation.dash && presentation.dash.length > 0
    ? ` stroke-dasharray="${presentation.dash.join(',')}"`
    : '';

  const shapeCommonAttrs = `fill="${escapeSvgAttr(presentation.fill)}" stroke="${escapeSvgAttr(presentation.stroke)}" stroke-width="${formatSvgNumber(presentation.strokeWidth)}" stroke-linejoin="round" stroke-linecap="round"${dashAttr}`;

  let shapeMarkup = '';
  switch (variant) {
    case 'rectangle':
      shapeMarkup = `<rect x="0" y="0" width="${formatSvgNumber(width)}" height="${formatSvgNumber(height)}" rx="4" ry="4" ${shapeCommonAttrs} />`;
      break;
    case 'circle': {
      const radius = Math.max(1, Math.min(width, height) / 2);
      shapeMarkup = `<circle cx="${formatSvgNumber(width / 2)}" cy="${formatSvgNumber(height / 2)}" r="${formatSvgNumber(radius)}" ${shapeCommonAttrs} />`;
      break;
    }
    case 'triangle':
      shapeMarkup = `<polygon points="${formatSvgNumber(width / 2)},0 ${formatSvgNumber(width)},${formatSvgNumber(height)} 0,${formatSvgNumber(height)}" ${shapeCommonAttrs} />`;
      break;
    case 'diamond':
      shapeMarkup = `<polygon points="${formatSvgNumber(width / 2)},0 ${formatSvgNumber(width)},${formatSvgNumber(height / 2)} ${formatSvgNumber(width / 2)},${formatSvgNumber(height)} 0,${formatSvgNumber(height / 2)}" ${shapeCommonAttrs} />`;
      break;
    case 'hexagon':
      shapeMarkup = `<polygon points="${buildPolygonPoints(6, width, height)}" ${shapeCommonAttrs} />`;
      break;
    case 'pentagon':
      shapeMarkup = `<polygon points="${buildPolygonPoints(5, width, height)}" ${shapeCommonAttrs} />`;
      break;
    case 'star':
      shapeMarkup = `<polygon points="${buildStarPoints(5, width, height)}" ${shapeCommonAttrs} />`;
      break;
    default:
      shapeMarkup = `<rect x="0" y="0" width="${formatSvgNumber(width)}" height="${formatSvgNumber(height)}" ${shapeCommonAttrs} />`;
  }

  const labelMarkup = renderShapeLabel(presentation, width, height, TEXT_PADDING);

  return shapeMarkup + labelMarkup;
};

const basicShapeProps = z.object({
  label: z.string().optional(),
  fillColor: z.string().optional(),
  strokeColor: z.string().optional(),
  strokeWidth: z.number().optional(),
});

export type BasicShapeProps = z.infer<typeof basicShapeProps>;

const createBasicShapeManifest = (
  id: BasicVariant,
  dimensions: { width: number; height: number }
): ShapeManifest<BasicShapeProps> => ({
  id,
  version: '1.0.0',
  category: 'basic',
  propsSchema: basicShapeProps,
  defaultProps: () => ({}),
  defaultDimensions: dimensions,
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: true,
  },
  renderers: {
    svg: (args) => renderBasicShapeSVG(id, dimensions, args),
  },
});

export const BasicShapeManifests: ShapeManifest<BasicShapeProps>[] = [
  createBasicShapeManifest('rectangle', { width: 120, height: 80 }),
  createBasicShapeManifest('circle', { width: 80, height: 80 }),
  createBasicShapeManifest('triangle', { width: 110, height: 95 }),
  createBasicShapeManifest('diamond', { width: 120, height: 120 }),
  createBasicShapeManifest('hexagon', { width: 130, height: 90 }),
  createBasicShapeManifest('pentagon', { width: 120, height: 90 }),
  createBasicShapeManifest('star', { width: 130, height: 130 }),
];
